import React, { useState, useEffect, useCallback } from 'react';
import { authFetch, authPost } from '../lib/authFetch';
import './NotificationBell.css';

function NotificationBell({ onSelectDonor, dataMode = 'real' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all', 'meetings', 'tasks'
  const [notifications, setNotifications] = useState([]);
  const [completingTask, setCompletingTask] = useState(null);

  // Fetch notifications from API
  const fetchNotifications = useCallback(async () => {
    try {
      const response = await authFetch(`/api/notifications?mode=${dataMode}`);
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
      }
    } catch (error) {
      console.log('Could not fetch notifications');
    }
  }, [dataMode]);

  // Complete a task
  const handleCompleteTask = async (e, notification) => {
    e.stopPropagation(); // Don't trigger the item click

    setCompletingTask(notification.id);

    try {
      const response = await authPost('/api/complete-task', {
        donorName: notification.donor,
        taskType: notification.type, // 'task' or 'meeting'
        taskDescription: notification.title,
        mode: dataMode
      });

      if (response.ok) {
        // Remove from local state immediately for snappy UI
        setNotifications(prev => prev.filter(n => n.id !== notification.id));
      }
    } catch (error) {
      console.error('Failed to complete task:', error);
    }

    setCompletingTask(null);
  };

  // Fetch on mount and periodically
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 5000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Refresh when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'all') return true;
    if (filter === 'meetings') return n.type === 'meeting';
    if (filter === 'tasks') return n.type === 'task';
    return true;
  });

  const unreadCount = notifications.length;

  const handleNotificationClick = (notification) => {
    if (notification.donor && onSelectDonor) {
      onSelectDonor(notification.donor);
      setIsOpen(false);
    }
  };

  return (
    <>
      <button
        className="notification-bell"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="View notifications"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <>
          <div
            className="notification-overlay"
            onClick={() => setIsOpen(false)}
          />
          <div className="notification-modal">
            <div className="notification-header">
              <h3>Upcoming</h3>
              <button
                className="notification-close"
                onClick={() => setIsOpen(false)}
                aria-label="Close notifications"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="notification-toggle">
              <button
                className={`toggle-btn ${filter === 'meetings' ? 'active' : ''}`}
                onClick={() => setFilter('meetings')}
              >
                Meetings
              </button>
              <button
                className={`toggle-btn ${filter === 'tasks' ? 'active' : ''}`}
                onClick={() => setFilter('tasks')}
              >
                Tasks
              </button>
            </div>
            <div className="notification-list">
              {filteredNotifications.length === 0 ? (
                <div className="notification-empty">
                  <p>No upcoming {filter === 'all' ? 'items' : filter}</p>
                  <p className="notification-empty-hint">
                    Add meetings and tasks to donors to see them here
                  </p>
                </div>
              ) : (
                filteredNotifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`notification-item ${completingTask === notification.id ? 'completing' : ''}`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <button
                      className="notification-icon-btn"
                      onClick={(e) => handleCompleteTask(e, notification)}
                      aria-label={`Mark ${notification.type} as complete`}
                    >
                      {completingTask === notification.id ? (
                        <div className="notification-icon loading">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" className="spinner" />
                          </svg>
                        </div>
                      ) : (
                        <div className="notification-icon">
                          {notification.type === 'task' ? (
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M9 11l3 3L22 4" />
                              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                            </svg>
                          ) : (
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                              <line x1="16" y1="2" x2="16" y2="6" />
                              <line x1="8" y1="2" x2="8" y2="6" />
                              <line x1="3" y1="10" x2="21" y2="10" />
                            </svg>
                          )}
                        </div>
                      )}
                    </button>
                    <div className="notification-content">
                      <div className="notification-title">{notification.title}</div>
                      <div className="notification-description">
                        {notification.description}
                      </div>
                      {notification.time && (
                        <div className="notification-time">{notification.time}</div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default NotificationBell;
